import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import { useEffect, useState } from 'react';
import {
    AppBar,
    Box,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tab,
    Tabs,
    Toolbar,
    Typography,
    useMediaQuery,
    type Theme,
} from '@mui/material';
import {
    CanAccess,
    LoadingIndicator,
    Logout,
    UserMenu,
    useCanAccess,
    useGetIdentity,
    useUserMenu,
} from 'react-admin';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { isAdministrator } from '@/components/filters/CreatedByUserFilter';
import { useConfigurationContext } from '@/root/ConfigurationContext';
import GlobalSearch from '@/modules/global_search/GlobalSearch';
import NotificationBell from '@/modules/notifications/NotificationBell';
import {
    buildFeatureNavItems,
    type NavItem,
} from '@/components/layout/navAccess';

const TOUCH_TARGET_SX = {
    '@media (hover: none) and (pointer: coarse)': {
        minHeight: 48,
        minWidth: 48,
    },
} as const;

const ROUTE_TITLES: { pattern: string; title: string }[] = [
    { pattern: '/', title: 'My metrics' },
    { pattern: '/quotes/folders/:projectId', title: 'Quotes in job folder' },
    { pattern: '/quotes/create', title: 'New quote' },
    { pattern: '/quotes/:id/show', title: 'Quote' },
    { pattern: '/quotes/:id', title: 'Edit quote' },
    { pattern: '/quotes', title: 'Quotes' },
    { pattern: '/projects/:id/show', title: 'Project' },
    { pattern: '/projects/:id', title: 'Edit project' },
    { pattern: '/projects', title: 'Projects' },
    { pattern: '/contractor_owners/create', title: 'New contractor' },
    { pattern: '/contractor_owners/:id/show', title: 'Contractor' },
    { pattern: '/contractor_owners/:id', title: 'Edit contractor' },
    { pattern: '/contractor_owners', title: 'Contractors' },
    { pattern: '/leads/create', title: 'New lead' },
    { pattern: '/leads/:id/show', title: 'Lead' },
    { pattern: '/leads/:id', title: 'Edit lead' },
    { pattern: '/leads', title: 'Leads' },
    { pattern: '/tickets/:id/show', title: 'Ticket' },
    { pattern: '/tickets', title: 'Tickets' },
    { pattern: '/cards/create', title: 'New card' },
    { pattern: '/cards/:id/show', title: 'Card' },
    { pattern: '/cards', title: 'Job Board' },
    { pattern: '/receipts/admin', title: 'Receipts admin' },
    { pattern: '/receipts/classes', title: 'QuickBooks Classes' },
    { pattern: '/receipts', title: 'Receipts' },
    { pattern: '/users', title: 'Users' },
    { pattern: '/roles', title: 'Roles' },
    { pattern: '/settings/site-config', title: 'Site config' },
    { pattern: '/settings/municipalities/create', title: 'New municipality' },
    { pattern: '/settings/municipalities/:id', title: 'Edit municipality' },
    { pattern: '/settings/municipalities', title: 'Municipalities' },
    { pattern: '/settings/help-knowledge/create', title: 'New help document' },
    { pattern: '/settings/help-knowledge/:id', title: 'Edit help document' },
    { pattern: '/settings/help-knowledge', title: 'Help knowledge' },
    { pattern: '/settings/profile', title: 'My info' },
    { pattern: '/settings', title: 'My info' },
    { pattern: '/project_file_configs', title: 'Project file configs' },
];

const pageTitleForPath = (pathname: string, appTitle: string) => {
    const match = ROUTE_TITLES.find(r => matchPath(r.pattern, pathname));
    return match ? `${match.title} | ${appTitle}` : appTitle;
};

export const navPathFor = (pathname: string): string | false => {
    if (matchPath({ path: '/', end: true }, pathname)) return '/';
    if (matchPath('/quotes/*', pathname)) return '/quotes';
    if (matchPath('/projects/*', pathname)) return '/projects';
    if (matchPath('/contractor_owners/*', pathname)) return '/contractor_owners';
    if (matchPath('/leads/*', pathname)) return '/leads';
    if (matchPath('/tickets/*', pathname)) return '/tickets';
    if (matchPath('/cards/*', pathname)) return '/cards';
    if (
        matchPath({ path: '/receipts', end: true }, pathname) ||
        matchPath('/receipts/*', pathname)
    ) {
        return '/receipts';
    }
    return false;
};

/** Tabs value must match a rendered Tab child, or MUI warns. */
export const navTabsValue = (
    path: string | false,
    items: ReadonlyArray<{ value: string }>
): string | false =>
    path !== false && items.some(item => item.value === path) ? path : false;

const Header = () => {
    const { logo, title } = useConfigurationContext();
    const location = useLocation();
    const [navOpen, setNavOpen] = useState(false);
    const [receiptsMenuAnchor, setReceiptsMenuAnchor] =
        useState<null | HTMLElement>(null);
    const isNarrow = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down('lg')
    );
    const { canAccess: canAccessTickets } = useCanAccess({
        resource: 'tickets',
        action: 'list',
    });
    const { canAccess: canAccessCards } = useCanAccess({
        resource: 'cards',
        action: 'list',
    });
    const { canAccess: canAccessLeads } = useCanAccess({
        resource: 'leads',
        action: 'list',
    });
    const { canAccess: canAccessQuotes } = useCanAccess({
        resource: 'quotes',
        action: 'list',
    });
    const { canAccess: canAccessProjects } = useCanAccess({
        resource: 'projects',
        action: 'list',
    });
    const { canAccess: canAccessContractors } = useCanAccess({
        resource: 'contractor_owners',
        action: 'list',
    });
    const { canAccess: canAccessReceiptUpload } = useCanAccess({
        resource: 'receipt',
        action: 'read',
    });
    const { canAccess: canViewDailyReceipts } = useCanAccess({
        resource: 'receipt',
        action: 'view_daily_receipts',
    });
    const { canAccess: canAdminReconciliation } = useCanAccess({
        resource: 'receipt',
        action: 'admin_reconciliation',
    });

    useEffect(() => {
        document.title = pageTitleForPath(
            location.pathname,
            title || 'MRT Dashboard'
        );
    }, [location.pathname, title]);

    useEffect(() => {
        setNavOpen(false);
        setReceiptsMenuAnchor(null);
    }, [location.pathname]);

    const currentPath = navPathFor(location.pathname);
    const featureItems = buildFeatureNavItems({
        leads: !!canAccessLeads,
        quotes: !!canAccessQuotes,
        projects: !!canAccessProjects,
        contractors: !!canAccessContractors,
        tickets: !!canAccessTickets,
        cards: !!canAccessCards,
        receiptUpload: !!canAccessReceiptUpload,
        receiptAdmin: !!canViewDailyReceipts || !!canAdminReconciliation,
    });
    const navItems: NavItem[] = [
        { label: 'Home', to: '/', value: '/' },
        ...featureItems,
    ];
    const receiptChildren =
        featureItems.find(item => item.value === '/receipts')?.children ?? [];
    const tabsValue = navTabsValue(currentPath, navItems);

    return (
        <Box component="nav" sx={{ flexGrow: 1 }}>
            <AppBar position="static" color="primary">
                <Toolbar variant="dense">
                    <Box
                        flex={1}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Box display="flex" alignItems="center">
                            <Box
                                display="flex"
                                alignItems="center"
                                component={Link}
                                to="/"
                                sx={{
                                    color: 'inherit',
                                    textDecoration: 'inherit',
                                }}
                                gap={1.5}
                            >
                                <Box
                                    component="img"
                                    sx={{ height: 24 }}
                                    src={logo}
                                    alt={title}
                                />
                                <Typography
                                    component="span"
                                    variant="h5"
                                    sx={{ display: { xs: 'none', sm: 'inline' } }}
                                >
                                    {title}
                                </Typography>
                            </Box>
                            {isNarrow && (
                                <IconButton
                                    color="inherit"
                                    aria-label="Open navigation"
                                    onClick={() => setNavOpen(true)}
                                    sx={TOUCH_TARGET_SX}
                                >
                                    <MenuIcon />
                                </IconButton>
                            )}
                        </Box>
                        {!isNarrow && (
                            <>
                                <Tabs
                                    value={tabsValue}
                                    aria-label="Navigation Tabs"
                                    indicatorColor="secondary"
                                    textColor="inherit"
                                >
                                    {navItems.map(item =>
                                        item.children?.length ? (
                                            <Tab
                                                key={item.value}
                                                label={
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        gap={0.25}
                                                    >
                                                        {item.label}
                                                        <ExpandMoreIcon
                                                            fontSize="small"
                                                        />
                                                    </Box>
                                                }
                                                value={item.value}
                                                onClick={event => {
                                                    event.preventDefault();
                                                    setReceiptsMenuAnchor(
                                                        event.currentTarget
                                                    );
                                                }}
                                            />
                                        ) : (
                                            <Tab
                                                key={item.value}
                                                label={item.label}
                                                component={Link}
                                                to={item.to}
                                                value={item.value}
                                            />
                                        )
                                    )}
                                </Tabs>
                                <Menu
                                    anchorEl={receiptsMenuAnchor}
                                    open={Boolean(receiptsMenuAnchor)}
                                    onClose={() => setReceiptsMenuAnchor(null)}
                                >
                                    {receiptChildren.map(child => (
                                        <MenuItem
                                            key={child.value}
                                            component={Link}
                                            to={child.to}
                                            selected={
                                                location.pathname === child.to
                                            }
                                            onClick={() =>
                                                setReceiptsMenuAnchor(null)
                                            }
                                        >
                                            {child.label}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </>
                        )}
                        <Box display="flex" alignItems="center">
                            <GlobalSearch />
                            <NotificationBell />
                            <LoadingIndicator />
                            <UserMenu>
                                <ConfigurationMenu />
                                <CanAccess resource="users" action="list">
                                    <UsersMenu />
                                </CanAccess>
                                <SiteConfigMenu />
                                <Logout />
                            </UserMenu>
                        </Box>
                    </Box>
                </Toolbar>
            </AppBar>
            <NavDrawer
                open={navOpen}
                onClose={() => setNavOpen(false)}
                items={navItems}
                currentPath={currentPath}
                pathname={location.pathname}
                title={title}
                logo={logo}
            />
        </Box>
    );
};

const NavDrawer = ({
    open,
    onClose,
    items,
    currentPath,
    pathname,
    title,
    logo,
}: {
    open: boolean;
    onClose: () => void;
    items: NavItem[];
    currentPath: string | false;
    pathname: string;
    title?: string;
    logo?: string;
}) => (
    <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: 280 } }}
    >
        <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={2}
            py={1.5}
        >
            <Box display="flex" alignItems="center" gap={1.5}>
                {logo && (
                    <Box
                        component="img"
                        sx={{ height: 24 }}
                        src={logo}
                        alt=""
                    />
                )}
                <Typography variant="h6">{title}</Typography>
            </Box>
            <IconButton
                onClick={onClose}
                aria-label="Close navigation"
                sx={TOUCH_TARGET_SX}
            >
                <CloseIcon />
            </IconButton>
        </Box>
        <Divider />
        <List>
            {items.map(item =>
                item.children?.length ? (
                    <Box key={item.value}>
                        <ListItemText
                            primary={item.label}
                            sx={{
                                px: 2,
                                pt: 1.5,
                                pb: 0.5,
                                color: 'text.secondary',
                            }}
                            primaryTypographyProps={{ variant: 'overline' }}
                        />
                        {item.children.map(child => (
                            <ListItemButton
                                key={child.value}
                                component={Link}
                                to={child.to}
                                selected={pathname === child.to}
                                onClick={onClose}
                                sx={{ ...TOUCH_TARGET_SX, pl: 3 }}
                            >
                                <ListItemText primary={child.label} />
                            </ListItemButton>
                        ))}
                    </Box>
                ) : (
                    <ListItemButton
                        key={item.value}
                        component={Link}
                        to={item.to}
                        selected={currentPath === item.value}
                        onClick={onClose}
                        sx={TOUCH_TARGET_SX}
                    >
                        <ListItemText primary={item.label} />
                    </ListItemButton>
                )
            )}
        </List>
    </Drawer>
);

const UsersMenu = () => {
    const { onClose } = useUserMenu() ?? {};
    return (
        <MenuItem component={Link} to="/users" onClick={onClose}>
            <ListItemIcon>
                <PeopleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Users</ListItemText>
        </MenuItem>
    );
};

const ConfigurationMenu = () => {
    const { onClose } = useUserMenu() ?? {};
    return (
        <MenuItem component={Link} to="/settings" onClick={onClose}>
            <ListItemIcon>
                <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>My info</ListItemText>
        </MenuItem>
    );
};

const SiteConfigMenu = () => {
    const { onClose } = useUserMenu() ?? {};
    const { identity } = useGetIdentity();
    if (!isAdministrator(identity)) return null;
    return (
        <MenuItem component={Link} to="/settings/site-config" onClick={onClose}>
            <ListItemIcon>
                <TuneIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Site config</ListItemText>
        </MenuItem>
    );
};

export default Header;
